<script lang="ts">
export default {
  name: 'WholesalerGroupOverview',
};
</script>

<script setup lang="ts">
import { computed, onMounted, ref, ComputedRef } from 'vue';
import { WholesalerGroup } from '@/types/WholesalerGroup';
import { favoriteService, wholesalerGroupService } from '@/services/api';
import FavoriteIndicator from '@/components/FavoriteIndicator.vue';
import { useRoute } from 'vue-router';
import { registerResizeItem } from '@/utilities/resize';
import { useEntityTabs } from '@/composables/useEntityTabs';
import type { TabConfig } from '@/types/EntityTabs';

const route = useRoute();
const { getTabs } = useEntityTabs();

// ------------
//     data
// ------------

const loading = ref(true);
const wholesalerGroup = ref({} as WholesalerGroup);
const favoriteProgress = ref(false);
let wholesalerTabs: ComputedRef<TabConfig[]> = computed(() => []);

// ------------
//    methods
// ------------

const toggleFavorite = async () => {
  favoriteProgress.value = true;
  if (!wholesalerGroup.value.favorite) {
    await favoriteService.remove('wholesalerGroup', wholesalerGroup.value.id);
    wholesalerGroup.value.favorite = false;
  } else {
    await favoriteService.create('wholesalerGroup', wholesalerGroup.value.id);
    wholesalerGroup.value.favorite = true;
  }
  favoriteProgress.value = false;
};

const refresh = async () => {
  loading.value = true;
  const id = route.params.id as string;
  wholesalerGroup.value =
    route.meta?.routeGroup === 'WholesalerGroupCustom'
      ? await wholesalerGroupService.customGroupById(id)
      : await wholesalerGroupService.findById(id);

  await loadWholesalerTabs();
  loading.value = false;
};

const loadWholesalerTabs = async () => {
  try {
    const id = route.params.id as string;
    wholesalerTabs = route.meta?.routeGroup === 'WholesalerGroupCustom'
      ? await getTabs('customWholesaler', id)
      : await getTabs('wholesaler', id);
  } catch (error) {
    console.error('Error loading wholesaler tabs:', error);
    wholesalerTabs = computed(() => []);
  }
};

// ------------
//    computed
// ------------

const isCustomGroup = computed(() => {
  return route.meta?.routeGroup === 'WholesalerGroupCustom';
});

// ------------
//   lifecycle
// ------------

onMounted(async () => {
  registerResizeItem({ name: 'wholesaler-header', selector: 'wholesaler-header' });
  registerResizeItem({ name: 'wholesaler-tabs', selector: 'wholesaler-tabs' });
  await refresh();
});
</script>

<template>
  <div>
    <div class="progress text-center" v-if="loading">
      <v-progress-circular :size="70" color="primary" indeterminate></v-progress-circular>
    </div>
    <div v-else>
      <div id="wholesaler-header" class="inner">
        <div class="tw:flex tw:items-center tw:gap-2.5">
          <h1>{{ wholesalerGroup.name }}</h1>
          <div v-if="!isCustomGroup" class="actions tw:flex tw:items-center">
            <FavoriteIndicator
              v-model="wholesalerGroup.favorite"
              @update:model-value="toggleFavorite"
              :loading="favoriteProgress" />
          </div>
        </div>
      </div>
      <div class="tw:border-b tw:border-l-0 tw:border-r-0 tw:border-t tw:border-solid tw:border-gray-200">
        <v-tabs
          id="wholesaler-tabs"
          show-arrows
          bg-color="grey-lighten-4"
          class="tw:uppercase"
          slide-color="primary"
          selected-class="tw:text-primary">
          <v-tab
            v-for="tab in wholesalerTabs"
            :key="tab.key"
            :ripple="false"
            class="tw:text-xs tw:text-gray-600"
            :to="tab.route"
          >
            {{ tab.label }}
          </v-tab>
        </v-tabs>
      </div>
      <router-view></router-view>
    </div>
  </div>
</template>

<style scoped>
.progress {
  display: grid;
  align-content: center;
  align-items: center;
  justify-content: center;
  justify-items: center;
  height: 50vh;
}
</style>
